import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, UserCircle, ShieldCheck } from "lucide-react";
import { cn } from "../../../design-system/lib/cn.js";
import Avatar from "../../../design-system/primitives/avatar/Avatar.jsx";
import Button from "../../../design-system/primitives/button/Button.jsx";
import { supabase } from "../../../supabaseClient.jsx";

const NAV_LINKS = [
  { href: "#hero", label: "Início" },
  { href: "#babas-profissionais", label: "Babás" },
  { href: "#depoimentos", label: "Depoimentos" },
  { href: "#como-funciona", label: "Como funciona" },
  { href: "#pacotes", label: "Pacotes" },
  { href: "#sobre-nos", label: "Sobre nós" },
];

export default function Header({ children, showNavLinks, customNavLinks }) {
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userType, setUserType] = useState(null);
  const [profileName, setProfileName] = useState(null);
  const [profileUrl, setProfileUrl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sessão real via Supabase
  useEffect(() => {
    const loadSession = async (session) => {
      if (!session) {
        setSession(null);
        setIsAdmin(false);
        setUserType(null);
        setProfileName(null);
        setProfileUrl(null);
        return;
      }
      setSession(session);
      const userId = session.user.id;
      try {
        const { data: usuario } = await supabase
          .from("usuarios")
          .select("tipo, is_admin, nome, foto_url")
          .eq("id", userId)
          .single();
        setUserType(usuario?.tipo || null);
        setIsAdmin(!!(usuario?.is_admin || usuario?.tipo === "admin"));
        setProfileName(usuario?.nome || session.user.email || null);
        setProfileUrl(usuario?.foto_url || session.user.user_metadata?.avatar_url || null);
      } catch {
        setUserType(null);
        setProfileName(session.user.email || null);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => loadSession(session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      loadSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    setDropOpen(false);
    setMenuOpen(false);
    await supabase.auth.signOut();
    setSession(null);
    setProfileName(null);
    setProfileUrl(null);
    setUserType(null);
    setIsAdmin(false);
    navigate("/");
  };

  const getDashboardPath = () => {
    if (isAdmin || userType === "admin") return "/dashboard-admin";
    if (userType === "cliente") return "/dashboard-familia";
    return "/dashboard-baba";
  };

  const links = customNavLinks || NAV_LINKS;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-warm-200 shadow-[0_10px_30px_rgba(26,20,15,0.08)]"
          : "bg-white/55 backdrop-blur-lg border-b border-transparent"
      )}
    >
      <div className="mx-auto flex h-18 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <button
          onClick={() => navigate("/")}
          className="shrink-0 rounded-xl px-2 py-1 transition hover:bg-white/70"
          aria-label="Ir para início"
        >
          <img
            src="/assets/logo-tesourinhos-trans.png"
            alt="Tesourinhos"
            className="h-10 w-auto"
          />
        </button>

        {/* Nav desktop */}
        {showNavLinks !== false && !session && (
          <nav className="hidden md:flex items-center gap-1 rounded-full border border-warm-200 bg-white/80 px-2 py-1.5 shadow-[0_8px_20px_rgba(30,22,16,0.05)]">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-full px-3.5 py-1.5 text-sm font-medium text-warm-700 transition-all hover:-translate-y-0.5 hover:bg-warm-100 hover:text-rose-400"
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}

        {/* Right side */}
        <div className="flex items-center gap-2">
          {children}

          {session ? (
            /* Logged-in dropdown */
            <div className="relative" ref={dropRef}>
              <button
                onClick={() => setDropOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-warm-200 bg-white/95 px-3 py-1.5 text-sm font-medium text-warm-700 shadow-[0_6px_16px_rgba(26,20,15,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(26,20,15,0.12)]"
              >
                <Avatar
                  src={profileUrl}
                  fallback={isAdmin ? "A" : (profileName?.charAt(0) || "U")}
                  size="sm"
                />
                <span className="max-w-25 truncate">
                  {isAdmin ? "Admin" : (profileName || "Meu perfil")}
                </span>
                <ChevronDown 
                  size={14} 
                  className={cn(
                    "transition-transform",
                    dropOpen ? "rotate-180" : ""
                  )} 
                />
              </button>

              {dropOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-2xl border border-warm-100 bg-white/95 py-1 shadow-[0_16px_32px_rgba(26,20,15,0.14)] backdrop-blur-lg">
                  <button
                    className="w-full px-4 py-2.5 text-left text-sm text-warm-700 transition-colors hover:bg-warm-50"
                    onClick={() => {
                      setDropOpen(false);
                      navigate(getDashboardPath());
                    }}
                  >
                    Meu Painel
                  </button>
                  <div className="mx-3 my-1 h-px bg-warm-100" />
                  <button
                    className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 transition-colors"
                    onClick={handleLogout}
                  >
                    Sair
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Desktop CTA */}
              <Button
                variant="default"
                size="md"
                className="hidden md:flex rounded-full px-5 shadow-[0_8px_20px_rgba(232,151,138,0.35)]"
                onClick={() => navigate("/login")}
              >
                Área dos assinantes
              </Button>

              {/* Mobile hamburger */}
              <button
                className="flex md:hidden items-center justify-center rounded-xl border border-warm-200 bg-white/85 p-2 text-warm-700 transition hover:bg-warm-100"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Menu"
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && !session && (
        <div className="border-t border-warm-100 bg-white/95 px-4 py-3 shadow-[0_14px_28px_rgba(26,20,15,0.12)] backdrop-blur-lg md:hidden">
          <nav className="flex flex-col gap-0.5 mb-3">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-xl px-3 py-2.5 text-sm font-medium text-warm-700 transition-colors hover:bg-warm-50"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <Button
            variant="default"
            size="md"
            className="w-full rounded-full"
            onClick={() => { setMenuOpen(false); navigate("/login"); }}
          >
            Área dos assinantes
          </Button>
        </div>
      )}
    </header>
  );
}